import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import React from "react";
import { CaseStatusType } from "../db/schema";
import {
  AudioFileInput,
  AudioUploadOptions,
  createAudioMetadata,
  createUrlRequestData,
  downloadAudioFile,
  extractFileFromInput,
  generateS3AudioUrl,
  getAudioErrorInfo,
  logAudioError,
  pollProcessingStatus,
  uploadAudioToS3,
  uploadSupplementalAudio,
  validateAudioFile,
  getFileExtension,
} from "../utils/audioUtils";
import UploadProgressToast from "../components/common/UploadProgressToast";
import { resetEditorForNewCase } from "../utilities/editorNewCaseReset";
import type { FileConfig as SharedFileConfig } from "../components/Menu/uploadFileConfig";

// Toast registry to track which toast belongs to which case
const caseToastRegistry = new Map<string, string>();

// Registry of in-flight upload abort controllers, keyed by caseId. Lets the
// "Cancel" button on the Add-to-Report overlay interrupt an in-progress upload
// (and the surrounding processMultiFileUpload pipeline) from outside the
// service.
const caseAbortControllerRegistry = new Map<string, AbortController>();

// Registry to track active upload file IDs for abandonment detection
const activeUploadFileIds = new Set<string>();

/**
 * Register a file ID as actively uploading
 */
const registerActiveUpload = (fileId: string) => {
  activeUploadFileIds.add(fileId);
};

/**
 * Unregister a file ID when upload completes or fails
 */
const unregisterActiveUpload = (fileId: string) => {
  activeUploadFileIds.delete(fileId);
};

/**
 * Clear all active uploads (e.g., on successful completion)
 */
const clearActiveUploads = () => {
  activeUploadFileIds.clear();
};

/**
 * Global handler for page unload - notifies server of abandoned uploads
 * Uses sendBeacon for reliable delivery during page unload
 */
const handleGlobalUnload = () => {
  if (activeUploadFileIds.size === 0) return;

  // Notify server about each abandoned file
  activeUploadFileIds.forEach((fileId) => {
    try {
      const url = `/api/cases/files/${fileId}/abandoned`;
      const data = JSON.stringify({ reason: "page_unload" });

      // sendBeacon is reliable during page unload, unlike fetch
      navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
    } catch (error) {
      // Best effort - can't do much if this fails
      console.error("Failed to notify server of abandoned upload:", error);
    }
  });

  // Clear the registry (page is unloading anyway)
  activeUploadFileIds.clear();
};

// Register the global unload handler once
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", handleGlobalUnload);
}

/**
 * Register a toast for a specific case
 */
const registerCaseToast = (caseId: string, toastId: string) => {
  caseToastRegistry.set(caseId, toastId);
};

/**
 * Dismiss toast for a specific case (called when case is deleted/trashed)
 */
export const dismissCaseToast = (caseId: string) => {
  const toastId = caseToastRegistry.get(caseId);
  if (toastId) {
    toast.dismiss(toastId);
    caseToastRegistry.delete(caseId);
  }
};

/**
 * Clean up toast registry entry (called when processing completes)
 */
export const cleanupCaseToast = (caseId: string) => {
  caseToastRegistry.delete(caseId);
};

/**
 * Abort an in-flight upload pipeline for a case. Triggered by the
 * Add-to-Report overlay's "Cancel" button so the upload thread tears down
 * cleanly (removes its beforeunload listener, skips the audioMetadata PATCH
 * that would otherwise re-set the intent that cancel just cleared).
 */
export const abortCaseUpload = (caseId: string) => {
  const controller = caseAbortControllerRegistry.get(caseId);
  if (controller) {
    controller.abort();
    caseAbortControllerRegistry.delete(caseId);
  }
};

/**
 * Create a case_files entry for single-file uploads
 * This ensures consistency with multi-file uploads in the case_files table
 */
const createCaseFileEntry = async (
  caseId: string,
  file: File | Blob,
  fileName: string,
  audioType: string,
  audioUrl: string,
  uploadOptions?: AudioUploadOptions
): Promise<string | undefined> => {
  try {
    const fileSize = file instanceof File ? file.size : (file as Blob).size;
    const fileType = file instanceof File ? file.type : "audio/m4a";

    const response = await axios.post(`/api/cases/${caseId}/files`, {
      file_url: audioUrl,
      file_name: fileName,
      file_size: `${(fileSize / 1024 / 1024).toFixed(2)} MB`,
      file_type: fileType,
      media_type: "audio",
      metadata: {
        audioType,
        context: uploadOptions?.additionalContext || "",
        ...(audioType === "interaction" &&
          uploadOptions?.speakers &&
          uploadOptions.speakers.length > 0 && {
            speakers: uploadOptions.speakers.map((s) => ({
              name: s.name,
              role: s.role || "",
            })),
          }),
      },
      order: 0,
    });

    return response.data.file?.id;
  } catch (error) {
    console.error("Error creating case file entry:", error);
    // Don't throw - this is a non-critical operation
    return undefined;
  }
};

/**
 * Check if a toast is already registered for a case (used to prevent duplicate toasts)
 */
export const hasCaseToast = (caseId: string): boolean => {
  return caseToastRegistry.has(caseId);
};

/**
 * Whether any audio file upload is currently in flight. Used by the inactivity
 * session timeout hook to avoid logging users out mid-upload when they have
 * slow internet — uploads stream direct to S3 and don't produce the mouse/API
 * activity that normally keeps the session alive.
 */
export const hasActiveUploads = (): boolean => {
  return activeUploadFileIds.size > 0;
};

interface AudioProcessingConfig {
  user?: any;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: number) => void;
  createNewCase?: boolean;
  existingCaseId?: string;
  setCaseContent?: (content: { data: string }) => void;
  onComplete?: () => void; // For checklist completion
  navigate?: (path: string) => void;
  onCancelled?: (caseId: string) => void; // Called when upload is cancelled
  reportTemplateId?: string; // Report template to use for AI generation
  callSign?: string | null; // Per-report call sign override stored on the case
  investigationId?: string | null; // Investigation linked for narrative context (add-on)
  // Intent controls what happens after all files are transcribed.
  // - undefined / "new_case": existing behavior — Bedrock multifile-initial generates the report.
  // - "add_to_report": The casesRouter completion handler runs the suggestions-insert
  //   Bedrock call server-side and persists the result to cases.pendingSuggestionsChanges.
  //   Nothing is reported back through the client polling — the user picks up the
  //   pending review the next time they load the case.
  intent?: "new_case" | "add_to_report";
}

// The shared shape (components/Menu/uploadFileConfig). `mediaType` is optional
// here: callers that predate documents do not set it, and audio is the default.
type FileConfig = Omit<SharedFileConfig, "mediaType"> & { mediaType?: SharedFileConfig["mediaType"] };

// PDFs, images, Word/text files and spreadsheets are read, not transcribed.
const isDocumentFile = (c: FileConfig) => c.mediaType === "image" || c.mediaType === "document";

interface MultiFileUploadOptions {
  includeTimestamps: boolean;
  detailLevel: "detailed" | "standard" | "concise";
}

/**
 * Handles multi-file upload processing
 */
const processMultiFileUpload = async (
  files: FileConfig[],
  options: MultiFileUploadOptions,
  config: Pick<
    AudioProcessingConfig,
    "user" | "onSuccess" | "onError" | "onProgress" | "createNewCase" | "existingCaseId" | "setCaseContent" | "onComplete" | "navigate" | "onCancelled" | "reportTemplateId" | "callSign" | "investigationId" | "intent"
  >
) => {
  const { user, onSuccess, onError, onProgress, createNewCase = true, existingCaseId, setCaseContent, onComplete, navigate, onCancelled, reportTemplateId, callSign, investigationId, intent } = config;

  if (files.length === 0) {
    onError?.("No files to upload");
    return;
  }

  // Validate all files first (skip Axon files and previous uploads - already validated)
  for (const fileConfig of files) {
    if (
      !fileConfig.axonSource &&
      !fileConfig.previousUpload &&
      !isDocumentFile(fileConfig) &&
      !validateAudioFile(fileConfig.file, fileConfig.audioType)
    ) {
      return;
    }
  }

  // Create AbortController for upload cancellation
  const abortController = new AbortController();
  let currentFileIndex = 0;
  let currentProgress = 0;
  let currentRemainingSeconds: number | undefined;
  let currentFileType: "narrative" | "interaction" = files[0]?.audioType || "narrative";
  let currentFileName = files[0]?.file.name || "";

  // Prevent accidental page refresh during upload
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
    return "";
  };
  window.addEventListener("beforeunload", handleBeforeUnload);

  // Helper to show cancelled state
  const showCancelledState = (toastId: string) => {
    toast.custom(
      () =>
        React.createElement(UploadProgressToast, {
          progress: 0,
          fileName: currentFileName,
          onCancel: () => {},
          cancelled: true,
          fileType: currentFileType,
          currentFileIndex: currentFileIndex + 1,
          totalFiles: files.length,
        }),
      { id: toastId, duration: 2000 }
    );
  };

  // Shared toast-cancel handler used by every UploadProgressToast onCancel
  // below. For Add to Report mode it kicks off the same server-side cleanup
  // the overlay's Cancel button does and notifies the editor via a window
  // event so its overlay closes immediately (rather than waiting up to 15s
  // for the CasesLayout poller to pick up the cleared state). The catch
  // block will also run the cleanup if abort() interrupts the pipeline —
  // both are safe since the cancel-add-to-report endpoint is idempotent.
  const handleToastCancel = () => {
    abortController.abort();
    if (intent === "add_to_report" && caseId) {
      const cancelledCaseId = caseId;
      dismissCaseToast(cancelledCaseId);
      axios
        .post(
          `/api/cases/${cancelledCaseId}/cancel-add-to-report`,
          {},
          { withCredentials: true },
        )
        .catch((err) => {
          console.error(
            "Failed to cancel Add to Report from toast:",
            err,
          );
        });
      // Notify the open editor (if any) so its "Generating changes" overlay
      // closes immediately and the case state is refreshed. The Editor
      // component listens for this event and matches by caseId.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("pnai:add-to-report-cancelled", {
            detail: { caseId: cancelledCaseId },
          }),
        );
      }
    } else {
      showCancelledState(toastId);
    }
  };

  // Helper to update toast with current progress
  const updateToast = (
    toastId: string,
    progress: number,
    remainingSeconds?: number,
    fileIndex?: number,
    fileType?: "narrative" | "interaction",
    fileName?: string,
    indeterminate?: boolean,
    statusText?: string
  ) => {
    currentProgress = progress;
    currentRemainingSeconds = remainingSeconds;
    if (fileIndex !== undefined) currentFileIndex = fileIndex;
    if (fileType) currentFileType = fileType;
    if (fileName) currentFileName = fileName;

    toast.custom(
      () =>
        React.createElement(UploadProgressToast, {
          progress,
          fileName: currentFileName,
          onCancel: handleToastCancel,
          remainingSeconds,
          fileType: currentFileType,
          currentFileIndex: currentFileIndex + 1,
          totalFiles: toastTotalFiles,
          indeterminate,
          statusText: statusText || toastStatusText,
          title: toastTitle,
        }),
      { id: toastId, duration: Infinity }
    );
  };

  // Track caseId outside try block so it's accessible in catch for cleanup
  let caseId: string | undefined;

  // Compute toast display values based on file mix
  const allAxon = files.every((f) => !!f.axonSource);
  const regularFileCount = files.filter((f) => !f.axonSource && !f.previousUpload).length;
  const axonFileCount = files.filter((f) => !!f.axonSource).length;
  const previousUploadCount = files.filter((f) => !!f.previousUpload).length;
  // "All previous uploads" — there's nothing to upload to S3, just server-side
  // calls to create case_files rows + mark them completed (and for add_to_report
  // batches, the server also runs Bedrock synchronously inside /completed). Use
  // an indeterminate toast with a different title so the user doesn't see a
  // misleading "Uploading audio" stuck at 0%.
  const allPreviousUploads = previousUploadCount > 0 && regularFileCount === 0 && axonFileCount === 0;
  let toastTotalFiles = allAxon || allPreviousUploads ? 1 : regularFileCount || 1;
  const statusParts: string[] = [];
  if (axonFileCount > 0) statusParts.push(`transferring ${axonFileCount} body cam file${axonFileCount > 1 ? "s" : ""} in background`);
  if (!allPreviousUploads && previousUploadCount > 0) statusParts.push(`reusing ${previousUploadCount} previous file${previousUploadCount > 1 ? "s" : ""}`);
  let toastStatusText: string | undefined;
  if (allAxon) {
    toastStatusText = "You can safely leave this page";
  } else if (allPreviousUploads) {
    toastStatusText = "Pulling existing transcriptions from prior uploads";
  } else if (statusParts.length > 0) {
    toastStatusText = `+ ${statusParts.join(", ")}`;
  }
  let toastTitle: string | undefined;
  if (allAxon) {
    toastTitle = "Transferring body cam video";
  } else if (allPreviousUploads) {
    toastTitle = `Reusing ${previousUploadCount} previous file${previousUploadCount > 1 ? "s" : ""}`;
  }

  // Create initial toast
  const firstRegularFile = files.find((f) => !f.axonSource && !f.previousUpload);
  const toastId = toast.custom(
    (t) =>
      React.createElement(UploadProgressToast, {
        progress: 0,
        fileName: allAxon
          ? `${files.length} body cam file${files.length > 1 ? "s" : ""}`
          : allPreviousUploads
            ? files[0]?.previousUpload?.fileName
            : firstRegularFile?.file.name || files[0]?.previousUpload?.fileName || files[0]?.file.name,
        onCancel: handleToastCancel,
        fileType: allAxon
          ? "interaction"
          : allPreviousUploads
            ? files[0]?.audioType
            : firstRegularFile?.audioType || files[0]?.audioType,
        currentFileIndex: 1,
        totalFiles: toastTotalFiles,
        indeterminate: allAxon || allPreviousUploads,
        statusText: toastStatusText,
        title: toastTitle,
      }),
    { duration: Infinity }
  );

  try {
    // Complete checklist if needed (for ChecklistView)
    if (onComplete) {
      await onComplete();
    }

    // Check file types for initial content message
    const hasAxonFilesInBatch = files.some((f) => !!f.axonSource);
    const hasRegularFilesInBatch = files.some((f) => !f.axonSource);
    // If only Axon files, user can leave immediately; if regular files, must stay for uploads
    const initialContent = hasRegularFilesInBatch
      ? "Uploading files — please keep this page open..."
      : "Transferring body cam footage...";

    // STEP 1: Create or update case
    if (!createNewCase && existingCaseId) {
      // Use existing case (e.g., from checklist or Add to Report)
      caseId = existingCaseId;

      // Register toast for this case so it can be dismissed if case is deleted
      registerCaseToast(caseId, toastId);
      // Register the abort controller so the Add-to-Report overlay's Cancel
      // button can interrupt this upload pipeline mid-flight.
      caseAbortControllerRegistry.set(caseId, abortController);

      // For "add_to_report" intent: don't change case status or overwrite content —
      // the user is actively reviewing the existing report. Just update audioMetadata
      // so the completion handler knows to skip the multifile-initial Bedrock call.
      if (intent === "add_to_report") {
        await axios.patch(`/api/cases/${caseId}`, {
          hasAttachments: true,
          audioMetadata: {
            source: "web",
            method: "multi-file",
            audioType: files.length === 1 ? files[0].audioType : "mixed",
            fileCount: files.length,
            intent,
            uploadOptions: {
              detailLevel: options.detailLevel,
              includeTimestamps: options.includeTimestamps,
            },
          },
        });
        // Don't call setCaseContent — leave the existing report visible
        // Don't navigate — user is already on the case
      } else {
        // Existing checklist/regenerate behavior — overwrite content with placeholder
        await axios.patch(`/api/cases/${caseId}`, {
          status: "processing",
          content: { data: initialContent },
          reportDetailLevel: options.detailLevel,
          includeTimestampsInNarrative: options.includeTimestamps,
          hasAttachments: true,
          audioMetadata: {
            source: "web",
            method: "multi-file",
            audioType: files.length === 1 ? files[0].audioType : "mixed",
            fileCount: files.length,
            uploadOptions: {
              detailLevel: options.detailLevel,
              includeTimestamps: options.includeTimestamps,
            },
          },
        });

        if (setCaseContent) {
          setCaseContent({ data: initialContent });
        }

        // Navigate to case
        if (navigate) {
          navigate(`/cases/${caseId}`);
        }
      }
    } else {
      // Create new case
      const pendingCase = await axios.post("/api/cases", {
        caseNumber: `case-${Date.now()}`,
        incidentDate: new Date().toISOString().split("T")[0],
        incidentTime: new Date().toISOString().split("T")[1].split("Z")[0],
        status: "processing",
        content: { data: initialContent },
        reportDetailLevel: options.detailLevel,
        includeTimestampsInNarrative: options.includeTimestamps,
        hasAttachments: true,
        ...(reportTemplateId && { reportTemplateId }),
        ...(callSign && { callSign }),
        ...(investigationId && { investigationId }),
        audioMetadata: {
          source: "web",
          method: "multi-file",
          audioType: files.length === 1 ? files[0].audioType : "mixed",
          fileCount: files.length,
          uploadOptions: {
            detailLevel: options.detailLevel,
            includeTimestamps: options.includeTimestamps,
          },
        },
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
      });

      caseId = pendingCase.data.case.id;
      resetEditorForNewCase();

      // Register toast for this case so it can be dismissed if case is deleted
      if (caseId) {
        registerCaseToast(caseId, toastId);
      }

      if (onSuccess) {
        onSuccess({
          ...pendingCase.data.case,
          status: CaseStatusType.enum.processing,
        });
      }

      // Navigate to case
      if (navigate) {
        navigate(`/cases/${caseId}`);
      }
    }

    // Track file IDs for audioMetadata update
    const uploadedFileIds: Array<{ fileId: string; audioType: string; fileType: string; fileSize: string }> = [];

    // Split files into regular (client-side upload), Axon (server-side transfer), and previous uploads
    const regularFiles = files.filter((f) => !f.axonSource && !f.previousUpload);
    const axonFiles = files.filter((f) => !!f.axonSource);
    const previousUploadFiles = files.filter((f) => !!f.previousUpload);

    // Assign order based on original position
    const fileOrderMap = new Map<FileConfig, number>();
    files.forEach((f, i) => fileOrderMap.set(f, i));

    // Process regular files sequentially (client must stay on page for S3 upload)
    const processRegularFiles = async () => {
      for (let i = 0; i < regularFiles.length; i++) {
        const fileConfig = regularFiles[i];
        const file = fileConfig.file;
        const order = fileOrderMap.get(fileConfig) ?? i;

        // Update toast for current file
        updateToast(toastId, 0, undefined, i, fileConfig.audioType, file.name);

        let fileId: string | undefined;

        try {
          if (isDocumentFile(fileConfig)) {
            // Read, not transcribed: upload, then queue text extraction. The
            // server re-posts this file's completion once the text is ready.
            const docEntry = await axios.post(`/api/cases/${caseId}/files`, {
              file_url: "",
              file_name: file.name,
              file_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
              file_type: file.type || "application/octet-stream",
              media_type: fileConfig.mediaType,
              metadata: { context: fileConfig.additionalContext },
              order,
            });
            fileId = docEntry.data.file.id;
            if (fileId) registerActiveUpload(fileId);

            const docUrl = await axios.post("/api/get-upload-url", {
              fileName: file.name,
              fileType: file.type || "application/octet-stream",
              uploadType: "case-document",
            });
            await uploadAudioToS3(
              file,
              docUrl.data.uploadURL,
              (progress, remainingSeconds) => {
                updateToast(toastId, progress, remainingSeconds, i, fileConfig.audioType, file.name);
                onProgress?.(progress);
              },
              abortController.signal
            );
            await axios.patch(`/api/cases/files/${fileId}/metadata`, {
              file_url: generateS3AudioUrl(docUrl.data.key, "narrative"),
            });
            if (fileId) unregisterActiveUpload(fileId);
            await axios.post(`/api/cases/files/${fileId}/process-document`);

            if (fileId) {
              uploadedFileIds.push({
                fileId,
                audioType: fileConfig.audioType,
                fileType: file.type,
                fileSize: `${(file.size / 1000000).toFixed(2)}MB`,
              });
            }
            continue;
          }

          const fileExtension = getFileExtension(file);

          const fileEntryResponse = await axios.post(`/api/cases/${caseId}/files`, {
            file_url: "",
            file_name: file.name,
            file_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            file_type: file.type || `audio/${fileExtension}`,
            media_type: "audio",
            metadata: {
              audioType: fileConfig.audioType,
              context: fileConfig.additionalContext,
              ...(fileConfig.keepVideo && { keepVideo: true }),
              ...(fileConfig.videoAnalysis && {
                video: { requested: true, status: "pending", mode: fileConfig.videoAnalysis.mode, analyzeAll: fileConfig.videoAnalysis.analyzeAll },
              }),
              // Tag every file from an Add-to-Report batch so the server-side
              // completion handler can identify which files belong to the batch.
              ...(intent === "add_to_report" && {
                source: "suggestions_insert",
                intent: "add_to_report",
              }),
              ...(fileConfig.audioType === "interaction" &&
                fileConfig.speakers.length > 0 && {
                  speakers: fileConfig.speakers.map((s) => ({
                    name: s.name,
                    role: s.role || "",
                  })),
                  speakerMap: fileConfig.speakers.reduce((acc, speaker, idx) => {
                    acc[`spk_${idx}`] = {
                      name: speaker.name,
                      initials: speaker.name
                        .split(" ")
                        .map((n) => n[0])
                        .join(""),
                    };
                    return acc;
                  }, {} as Record<string, { name: string; initials: string }>),
                }),
              ...(fileConfig.languages?.length > 0 && {
                languages: fileConfig.languages.filter((l) => l),
              }),
            },
            order,
          });

          fileId = fileEntryResponse.data.file.id;

          if (fileId) {
            registerActiveUpload(fileId);
          }

          const urlRequestData = {
            userId: user?.id,
            fileName: file.name,
            audioType: fileConfig.audioType,
            caseId: caseId,
            fileId: fileId,
            userName: user?.name,
            badgeId: user?.badgeID,
            localDateTimeOffset: new Date()
              .toLocaleString()
              .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
            uploadOptions: {
              detailLevel: options.detailLevel,
              includeTimestamps: options.includeTimestamps,
              additionalContext: fileConfig.additionalContext,
              ...(fileConfig.audioType === "interaction" &&
                fileConfig.speakers.length > 0 && {
                  speakers: fileConfig.speakers,
                }),
            },
          };

          const urlResponse = await axios.post("/api/get-upload-url", urlRequestData);
          const uploadData = urlResponse.data;

          await uploadAudioToS3(
            file,
            uploadData.uploadURL,
            (progress, remainingSeconds) => {
              updateToast(toastId, progress, remainingSeconds, i, fileConfig.audioType, file.name);
              onProgress?.(progress);
            },
            abortController.signal
          );

          await axios.patch(`/api/cases/files/${fileId}/metadata`, {
            file_url: generateS3AudioUrl(uploadData.key, fileConfig.audioType),
          });

          if (fileId) {
            unregisterActiveUpload(fileId);
          }

          if (fileConfig.audioType === "narrative") {
            await axios.post("/api/transcribe", {
              key: uploadData.key,
              caseId: caseId,
              fileId: fileId,
              localDateTimeOffset: new Date()
                .toLocaleString()
                .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
              uploadOptions: {
                detailLevel: options.detailLevel,
                includeTimestamps: options.includeTimestamps,
                additionalContext: fileConfig.additionalContext,
              },
            });
          } else if (fileConfig.audioType === "interaction") {
            await axios.post(`/api/cases/files/${fileId}/trigger-processing`);
          }

          if (fileId) {
            uploadedFileIds.push({
              fileId,
              audioType: fileConfig.audioType,
              fileType: file.type,
              fileSize: `${(file.size / 1000000).toFixed(2)}MB`,
            });
          }
        } catch (fileError) {
          if (axios.isAxiosError(fileError) && fileError.code === "ERR_CANCELED") {
            throw fileError;
          }

          console.error(`Error uploading file ${file.name}:`, fileError);

          try {
            if (fileId) {
              unregisterActiveUpload(fileId);
              await axios.patch(`/api/cases/files/${fileId}/status`, {
                processing_status: "failed",
                error_message: fileError instanceof Error ? fileError.message : String(fileError),
              });
            }
          } catch (statusUpdateError) {
            console.error("Failed to update file status:", statusUpdateError);
          }

          toast.error(`Failed to upload ${file.name}, continuing with others...`, {
            duration: 3000,
          });
        }
      }
    };

    // Process Axon files in parallel (server-side transfers)
    // Track Axon transfer progress for toast updates
    let axonRemaining = axonFileCount;

    const updateAxonToast = () => {
      const label = axonRemaining === 0
        ? "All transfers complete"
        : `${axonRemaining} file${axonRemaining > 1 ? "s" : ""} remaining`;
      toast.custom(
        () =>
          React.createElement(UploadProgressToast, {
            progress: 0,
            fileName: label,
            onCancel: handleToastCancel,
            fileType: "interaction",
            currentFileIndex: 1,
            totalFiles: 1,
            indeterminate: axonRemaining > 0,
            statusText: axonRemaining > 0
              ? "You can safely leave this page"
              : undefined,
            title: "Transferring body cam video",
          }),
        { id: toastId, duration: Infinity }
      );
    };

    const processAxonFiles = async () => {
      if (axonFiles.length === 0) return;

      const transferPromises: Promise<void>[] = [];

      for (let j = 0; j < axonFiles.length; j++) {
        const axonConfig = axonFiles[j];
        const axon = axonConfig.axonSource!;
        const order = fileOrderMap.get(axonConfig) ?? j;

        try {
          const fileEntryResponse = await axios.post(`/api/cases/${caseId}/files`, {
            file_url: "",
            file_name: axon.title || "evidence",
            file_size: `${(axon.size / 1024 / 1024).toFixed(2)} MB`,
            file_type: axon.contentType || "video/mp4",
            media_type: "audio",
            metadata: {
              audioType: "interaction",
              context: axonConfig.additionalContext,
              // For Add-to-Report batches, override the source so the completion
              // handler can identify the batch. (axon evidenceId is still tracked.)
              source: intent === "add_to_report" ? "suggestions_insert" : "axon",
              ...(intent === "add_to_report" && { intent: "add_to_report" }),
              evidenceId: axon.evidenceId,
              ...(axonConfig.keepVideo && { keepVideo: true }),
              ...(axonConfig.videoAnalysis && {
                video: { requested: true, status: "pending", mode: axonConfig.videoAnalysis.mode, analyzeAll: axonConfig.videoAnalysis.analyzeAll },
              }),
              ...(axonConfig.speakers.length > 0 && {
                speakers: axonConfig.speakers.map((s) => ({
                  name: s.name,
                  role: s.role || "",
                })),
                speakerMap: axonConfig.speakers.reduce((acc, speaker, idx) => {
                  acc[`spk_${idx}`] = {
                    name: speaker.name,
                    initials: speaker.name
                      .split(" ")
                      .map((n) => n[0])
                      .join(""),
                  };
                  return acc;
                }, {} as Record<string, { name: string; initials: string }>),
              }),
              ...(axonConfig.languages?.length > 0 && {
                languages: axonConfig.languages.filter((l: string) => l),
              }),
            },
            order,
          });

          const axonFileId = fileEntryResponse.data.file.id;

          uploadedFileIds.push({
            fileId: axonFileId,
            audioType: "interaction",
            fileType: axon.contentType || "video/mp4",
            fileSize: `${(axon.size / 1000000).toFixed(2)}MB`,
          });

          transferPromises.push(
            axios.post(
              `/api/evidence/${axon.evidenceId}/transfer`,
              { title: axon.title, caseId, fileId: axonFileId },
              { signal: abortController.signal }
            ).then(() => {
              console.log(`Axon transfer completed for ${axon.title}`);
              axonRemaining--;
              if (axonRemaining > 0) {
                toastStatusText = `+ transferring ${axonRemaining} body cam file${axonRemaining > 1 ? "s" : ""} in background`;
              } else {
                toastStatusText = undefined;
              }
              updateAxonToast();
            }).catch((err) => {
              if (axios.isAxiosError(err) && err.code === "ERR_CANCELED") return;
              console.error(`Axon transfer failed for ${axon.title}:`, err);
              axonRemaining--;
              if (axonRemaining > 0) {
                toastStatusText = `+ transferring ${axonRemaining} body cam file${axonRemaining > 1 ? "s" : ""} in background`;
              } else {
                toastStatusText = undefined;
              }
              updateAxonToast();
              axios.patch(`/api/cases/files/${axonFileId}/status`, {
                processing_status: "failed",
                error_message: err instanceof Error ? err.message : String(err),
              }).catch(() => {});
            })
          );
        } catch (entryError) {
          if (axios.isAxiosError(entryError) && entryError.code === "ERR_CANCELED") return;
          console.error(`Failed to create file entry for ${axon.title}:`, entryError);
          toast.error(`Failed to start transfer for ${axon.title}`, { duration: 3000 });
        }
      }

      await Promise.allSettled(transferPromises);
    };

    // Process previous upload files (fast — no client-side upload needed)
    const processPreviousUploadFiles = async () => {
      for (const fileConfig of previousUploadFiles) {
        const prev = fileConfig.previousUpload!;
        const order = fileOrderMap.get(fileConfig) ?? 0;
        const sameAudioType = fileConfig.audioType === prev.originalAudioType;

        try {
          if (isDocumentFile(fileConfig)) {
            // A document already in the library points at the same object. Its
            // extracted text is already beside it, so the runner finishes at once.
            const docEntry = await axios.post(`/api/cases/${caseId}/files`, {
              file_url: prev.fileUrl,
              file_name: prev.fileName,
              file_size: prev.fileSize,
              file_type: fileConfig.file.type || "application/octet-stream",
              media_type: fileConfig.mediaType,
              metadata: {
                context: fileConfig.additionalContext,
                source: "previous_upload",
                sourceCaseFileId: prev.caseFileId,
              },
              order,
            });
            const docFileId = docEntry.data.file.id;
            await axios.post(`/api/cases/files/${docFileId}/process-document`);
            uploadedFileIds.push({
              fileId: docFileId,
              audioType: fileConfig.audioType,
              fileType: fileConfig.file.type,
              fileSize: prev.fileSize,
            });
            continue;
          }

          // Create case_file entry
          const fileEntryResponse = await axios.post(`/api/cases/${caseId}/files`, {
            file_url: prev.fileUrl,
            file_name: prev.fileName,
            file_size: prev.fileSize,
            file_type: fileConfig.file.type || "audio/mp4",
            media_type: "audio",
            metadata: {
              audioType: fileConfig.audioType,
              context: fileConfig.additionalContext,
              // For Add-to-Report batches, override the source so the completion
              // handler can identify the batch.
              source: intent === "add_to_report" ? "suggestions_insert" : "previous_upload",
              ...(intent === "add_to_report" && { intent: "add_to_report" }),
              sourceCaseFileId: prev.caseFileId,
              originalAudioType: prev.originalAudioType,
              ...(fileConfig.audioType !== prev.originalAudioType && {
                audioTypeChanged: true,
              }),
              // Footage analysis needs the frames Lambda to run on a fresh
              // copy, so the file goes through the re-process path below
              // rather than reusing the old transcription.
              ...(fileConfig.videoAnalysis && {
                keepVideo: true,
                video: { requested: true, status: "pending", mode: fileConfig.videoAnalysis.mode, analyzeAll: fileConfig.videoAnalysis.analyzeAll },
              }),
              ...(sameAudioType && prev.transcriptionUrl && !fileConfig.videoAnalysis && {
                transcriptionUrl: prev.transcriptionUrl,
              }),
              ...(fileConfig.audioType === "interaction" &&
                fileConfig.speakers.length > 0 && {
                  speakers: fileConfig.speakers.map((s) => ({
                    name: s.name,
                    role: s.role || "",
                  })),
                  speakerMap: fileConfig.speakers.reduce((acc, speaker, idx) => {
                    acc[`spk_${idx}`] = {
                      name: speaker.name,
                      initials: speaker.name.split(" ").map((n) => n[0]).join(""),
                    };
                    return acc;
                  }, {} as Record<string, { name: string; initials: string }>),
                }),
              ...(fileConfig.languages?.length > 0 && {
                languages: fileConfig.languages.filter((l) => l),
              }),
            },
            order,
          });

          const fileId = fileEntryResponse.data.file.id;

          if (sameAudioType && prev.transcriptionUrl && !fileConfig.videoAnalysis) {
            // REUSE PATH: Same audio type with existing transcription — skip re-upload and re-transcription
            await axios.post(`/api/cases/${caseId}/files/${fileId}/completed`, {
              transcriptionUrl: prev.transcriptionUrl,
            });
          } else {
            // RE-PROCESS PATH: Different audio type — server copies S3 file and triggers appropriate Lambda
            await axios.post(`/api/cases/files/${fileId}/reuse`, {
              targetCaseId: caseId,
              targetAudioType: fileConfig.audioType,
              sourceCaseFileId: prev.caseFileId,
            });
          }

          uploadedFileIds.push({
            fileId,
            audioType: fileConfig.audioType,
            fileType: fileConfig.file.type || "audio/mp4",
            fileSize: prev.fileSize,
          });
        } catch (error) {
          console.error(`Error processing previous upload ${prev.fileName}:`, error);
          toast.error(`Failed to process ${prev.fileName}`, { duration: 3000 });
        }
      }
    };

    // STEP 2: Run regular uploads, Axon transfers, and previous uploads in parallel
    // Always run previous uploads processing (fast, no client-side upload)
    const previousUploadsPromise = previousUploadFiles.length > 0
      ? processPreviousUploadFiles()
      : Promise.resolve();

    if (regularFiles.length > 0 && axonFiles.length > 0) {
      // Mixed: run all concurrently
      const [regularResult] = await Promise.allSettled([
        processRegularFiles().then(() => {
          // Regular uploads done — user can leave now
          window.removeEventListener("beforeunload", handleBeforeUnload);
          clearActiveUploads();
          // Switch toast to show Axon transfers still running
          toastTotalFiles = 1;
          toastStatusText = "You can safely leave this page";
          toastTitle = "Transferring body cam video";
          updateAxonToast();
        }),
        processAxonFiles(),
        previousUploadsPromise,
      ]);

      // If regular uploads were cancelled, re-throw so outer catch handles cleanup
      if (regularResult.status === "rejected") {
        throw regularResult.reason;
      }
    } else if (axonFiles.length > 0) {
      // All Axon (+ possibly previous uploads): fire all transfers
      await Promise.allSettled([processAxonFiles(), previousUploadsPromise]);
    } else if (regularFiles.length > 0) {
      // Regular + possibly previous uploads
      const [regularResult] = await Promise.allSettled([processRegularFiles(), previousUploadsPromise]);

      // A cancelled upload must reach the outer catch (cancel-processing +
      // Trash). Swallowing it here let the pipeline carry on to "Processing
      // audio files..." after the officer pressed Cancel upload.
      if (regularResult.status === "rejected") {
        throw regularResult.reason;
      }
    } else {
      // Only previous uploads (no regular or Axon files)
      await previousUploadsPromise;
    }

    // Remove beforeunload listener - uploads complete
    window.removeEventListener("beforeunload", handleBeforeUnload);

    // Clear all active upload tracking since uploads completed successfully
    clearActiveUploads();

    // Drop the abort controller registry entry — pipeline is past the
    // interruptible phase.
    if (caseId) {
      caseAbortControllerRegistry.delete(caseId);
    }

    // Notify the open editor (if any) that the local upload phase is done
    // for this case, so the "Generating changes" overlay can switch its
    // message from "keep this page open while uploading" to "you can safely
    // leave this page". The Editor component listens for this event and
    // matches by caseId.
    if (typeof window !== "undefined" && caseId) {
      window.dispatchEvent(
        new CustomEvent("pnai:add-to-report-upload-complete", {
          detail: { caseId },
        }),
      );
    }

    // If the user pressed Cancel from the overlay, dismissCaseToast removed
    // the registry entry. Skip the audioMetadata PATCH below — it would
    // re-set audioMetadata.intent (and resurrect the "Generating changes"
    // overlay after a refresh) right after the cancel endpoint just cleared it.
    const cancelledByUser = !!caseId && !caseToastRegistry.has(caseId);
    if (cancelledByUser) {
      console.log(
        `[add_to_report] Pipeline finished but user cancelled — skipping audioMetadata PATCH and toast for case ${caseId}`,
      );
      return;
    }

    // Update audioMetadata with case_files IDs now that all files are created
    if (caseId && uploadedFileIds.length > 0) {
      try {
        await axios.patch(`/api/cases/${caseId}`, {
          audioMetadata: {
            source: "web",
            method: "multi-file",
            audioType: files.length === 1 ? files[0].audioType : "mixed",
            fileCount: files.length,
            files: uploadedFileIds,
            ...(intent && { intent }),
            uploadOptions: {
              detailLevel: options.detailLevel,
              includeTimestamps: options.includeTimestamps,
            },
          },
        });
      } catch (err) {
        console.error("Failed to update audioMetadata with file IDs:", err);
      }
    }

    // All files uploaded/transferred and processing triggered.
    // For "add_to_report" mode, don't overwrite case content — the user is reviewing
    // the existing report and we don't want a "Processing audio files..." placeholder
    // to flash in the editor.
    if (caseId && intent !== "add_to_report") {
      try {
        await axios.patch(`/api/cases/${caseId}`, {
          content: { data: "Processing audio files..." },
        });
        if (onSuccess) {
          onSuccess({ id: caseId, content: { data: "Processing audio files..." }, status: "processing" });
        }
      } catch (err) {
        console.error("Failed to update case content to processing:", err);
      }
    }

    if (!caseId) {
      throw new Error("No case ID available for processing status polling");
    }

    // For "add_to_report" mode, dismiss the upload progress toast and let the
    // Editor's "Generating changes" overlay take over as the user-facing
    // feedback. We can't use pollProcessingStatus here because the case
    // status stays "active" the whole time (we never flip it to "processing"),
    // so the poller would falsely report success on the first tick. CasesLayout
    // already polls /:caseId/pending-suggestions-changes for the actual signal.
    if (intent === "add_to_report") {
      // Guard against the user cancelling mid-flight: dismissCaseToast (called
      // by the overlay's Cancel button) removes the registry entry. If it's
      // gone, the user already cancelled — skip the success toast so we don't
      // clobber the dismissal.
      if (caseToastRegistry.has(caseId)) {
        toast.success(
          "Audio uploaded. Generating changes — you can keep working.",
          { id: toastId, duration: 4000 },
        );
        cleanupCaseToast(caseId);
      }
      return;
    }

    const uploadMessage = files.length > 1
      ? `All files uploaded. Processing ${files.length} files...`
      : "File uploaded. Processing audio...";
    toast.loading(uploadMessage, { id: toastId, duration: Infinity });

    // Start polling for status updates
    pollProcessingStatus(caseId, {
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess(
            data.case
              ? {
                  case: data.case,
                  score: data.score,
                  suggestions: data.suggestions,
                  transcription: data.transcription,
                }
              : data
          );
        }
      },
      onError: (error) => {
        console.error("Processing failed:", error);
        onError?.(error);
      },
      toastId,
      successMessage:
        files.length > 1
          ? "Case created with multiple files"
          : "Narrative generated",
      cleanupToast: cleanupCaseToast,
    });
  } catch (error) {
    // Remove beforeunload listener - upload failed/cancelled
    window.removeEventListener("beforeunload", handleBeforeUnload);

    // Clear active uploads tracking
    clearActiveUploads();

    // Drop the abort controller registry entry on the error path too.
    if (caseId) {
      caseAbortControllerRegistry.delete(caseId);
    }

    // Handle upload cancellation
    if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
      console.log("Multi-file upload cancelled by user");

      // For Add to Report cancellations: run the same server-side cleanup
      // the overlay's Cancel button does (delete S3 files, delete case_files,
      // clear intent + pending blob). Both the toast's X and the overlay end
      // up here via abortController.abort(); the cancel-add-to-report
      // endpoint is idempotent so calling it twice is harmless if the overlay
      // already cancelled.
      if (intent === "add_to_report" && caseId) {
        try {
          await axios.post(
            `/api/cases/${caseId}/cancel-add-to-report`,
            {},
            { withCredentials: true },
          );
        } catch (cancelError) {
          console.error(
            "Failed to cancel Add to Report from upload pipeline:",
            cancelError,
          );
        }
        // Hide the upload toast (idempotent if the overlay already dismissed it).
        dismissCaseToast(caseId);
        return;
      }

      // Show cancelled state in toast
      showCancelledState(toastId);

      // Cancel all processing: delete S3 files, abort Transcribe jobs, discard case
      if (caseId && createNewCase) {
        try {
          await axios.post(`/api/cases/${caseId}/cancel-processing`, {}, { withCredentials: true });
          console.log("Case processing cancelled and discarded:", caseId);
          cleanupCaseToast(caseId);
          onCancelled?.(caseId);
        } catch (cancelError) {
          console.error("Failed to cancel processing:", cancelError);
          // Fallback: try simple discard
          try {
            await axios.patch(`/api/cases/${caseId}/discard`, {}, { withCredentials: true });
            cleanupCaseToast(caseId);
            onCancelled?.(caseId);
          } catch (discardError) {
            console.error("Failed to discard case:", discardError);
          }
        }
      }

      return; // Exit gracefully
    }

    console.error("Error in multi-file upload:", error);
    logAudioError(
      error,
      "AudioProcessingService",
      "processMultiFileUpload",
      files[0]?.file || new Blob(),
      { fileCount: files.length }
    );

    const errorInfo = getAudioErrorInfo(error);
    toast.error(errorInfo.errorMessage, { id: toastId });
    onError?.(errorInfo.errorMessage);
  }
};

/**
 * Handles audio file processing for both new case creation and existing case updates
 */
export const processAudioFile = async (
  fileOrEvent: AudioFileInput | FileConfig[],
  audioType: string | MultiFileUploadOptions = "narrative",
  methodOrOptions?: string | AudioUploadOptions,
  supplementalAudio?: File | null,
  config: AudioProcessingConfig = {}
) => {
  const {
    user,
    onSuccess,
    onError,
    onProgress,
    createNewCase = true,
    existingCaseId,
    setCaseContent,
    onComplete,
    navigate,
    onCancelled,
    reportTemplateId,
    callSign,
    investigationId,
    intent,
  } = config;

  // MULTI-FILE UPLOAD PATH
  if (Array.isArray(fileOrEvent) && fileOrEvent.length > 0) {
    return processMultiFileUpload(
      fileOrEvent,
      audioType as MultiFileUploadOptions,
      {
        user,
        onSuccess,
        onError,
        onProgress,
        createNewCase,
        existingCaseId,
        setCaseContent,
        onComplete,
        navigate,
        onCancelled,
        reportTemplateId,
        callSign,
        investigationId,
        intent,
      }
    );
  }

  // SINGLE-FILE PATH - audioType must be a string here
  const audioTypeStr = audioType as string;

  let method = "upload";
  let uploadOptions: AudioUploadOptions | undefined;

  // Handle different call signatures
  if (typeof methodOrOptions === "string") {
    method = methodOrOptions;
  } else if (methodOrOptions && typeof methodOrOptions === "object") {
    uploadOptions = methodOrOptions;
    if (methodOrOptions.method) {
      method = methodOrOptions.method;
    }
  }

  // Extract file from input
  const file = extractFileFromInput(fileOrEvent as AudioFileInput);
  if (!file) return;

  // Validate file
  if (!validateAudioFile(file, audioTypeStr)) {
    return;
  }

  // Complete checklist if needed (for ChecklistView)
  if (onComplete) {
    await onComplete();
  }

  // Create AbortController for upload cancellation
  const abortController = new AbortController();
  let currentProgress = 0;
  let currentRemainingSeconds: number | undefined;

  // Prevent accidental page refresh during upload
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = ""; // Required for Chrome
    return ""; // Required for some browsers
  };

  // Add warning when upload starts
  window.addEventListener("beforeunload", handleBeforeUnload);

  // Helper to show cancelled state
  const showCancelledState = (toastId: string) => {
    toast.custom(
      (t) =>
        React.createElement(UploadProgressToast, {
          progress: 0,
          fileName: file instanceof File ? file.name : undefined,
          onCancel: () => {},
          cancelled: true,
        }),
      { id: toastId, duration: 2000 } // Auto-dismiss after 2 seconds
    );
  };

  // Helper to update toast with current progress
  const updateToast = (
    toastId: string,
    progress: number,
    remainingSeconds?: number
  ) => {
    currentProgress = progress;
    currentRemainingSeconds = remainingSeconds;
    toast.custom(
      (t) =>
        React.createElement(UploadProgressToast, {
          progress,
          fileName: file instanceof File ? file.name : undefined,
          onCancel: () => {
            abortController.abort();
            showCancelledState(toastId);
          },
          remainingSeconds,
        }),
      { id: toastId, duration: Infinity }
    );
  };

  // Track caseId and fileId outside try block so they're accessible in catch for cleanup
  let caseId: string | undefined = existingCaseId;
  let singleFileId: string | undefined; // Track fileId for single-file uploads

  // Create initial toast
  const toastId = toast.custom(
    (t) =>
      React.createElement(UploadProgressToast, {
        progress: 0,
        fileName: file instanceof File ? file.name : undefined,
        onCancel: () => {
          abortController.abort();
          showCancelledState(t.id);
        },
      }),
    {
      duration: Infinity,
    }
  );

  try {
    let key: string | undefined; // Change to allow undefined initially

    // For INTERACTION audio with NEW cases, upload FIRST, then create case
    if (createNewCase && audioTypeStr === "interaction") {
      const fileExtension = getFileExtension(file);

      // Get upload URL WITHOUT caseId (we'll create the case after upload)
      const urlRequestData = {
        userId: user?.id,
        fileName:
          file instanceof File
            ? file.name
            : `recorded_audio_${user?.id}_${
                new Date().toISOString().split("T")[0]
              }.${fileExtension}`,
        audioType: audioTypeStr,
        // Don't include caseId yet - we'll create it after upload
        userName: user?.name,
        badgeId: user?.badgeID,
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
        ...(uploadOptions && {
          uploadOptions: {
            detailLevel: uploadOptions.detailLevel,
            includeTimestamps: uploadOptions.includeTimestamps,
            additionalContext: uploadOptions.additionalContext,
            ...(uploadOptions.speakers?.length > 0 && {
              speakers: uploadOptions.speakers,
            }),
          },
        }),
      };

      const urlResponse = await axios.post(
        "/api/get-upload-url",
        urlRequestData
      );
      const uploadData = urlResponse.data;
      key = uploadData.key;

      // Upload the file to S3 first
      await uploadAudioToS3(
        file,
        uploadData.uploadURL,
        (progress, remainingSeconds) => {
          updateToast(toastId, progress, remainingSeconds);
          onProgress?.(progress);
        },
        abortController.signal
      );

      // NOW create the case with the S3 key
      // Note: audioUrl is NOT set on the case - it's stored in case_files instead
      const pendingCase = await axios.post("/api/cases", {
        caseNumber: `case-${Date.now()}`,
        incidentDate: new Date().toISOString().split("T")[0],
        incidentTime: new Date().toISOString().split("T")[1].split("Z")[0],
        status: "processing",
        content: { data: "Processing audio..." },
        audioMetadata: createAudioMetadata(
          file,
          method,
          audioTypeStr,
          uploadOptions,
          supplementalAudio
        ),
        audioKey: key, // Pass the S3 key so backend can trigger processing
        hasAttachments: true, // Audio URL stored in case_files, not on case
        ...(reportTemplateId && { reportTemplateId }),
        ...(callSign && { callSign }),
        ...(investigationId && { investigationId }),
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
      });
      caseId = pendingCase.data.case.id;
      resetEditorForNewCase();

      // Register toast for this case so it can be dismissed if case is deleted
      if (caseId) {
        registerCaseToast(caseId, toastId);

        // Create case_files entry for consistency with multi-file uploads
        const fileName =
          file instanceof File
            ? file.name
            : `recorded_audio_${user?.id}_${new Date().toISOString().split("T")[0]}.${getFileExtension(file)}`;
        singleFileId = await createCaseFileEntry(
          caseId,
          file,
          fileName,
          audioTypeStr,
          generateS3AudioUrl(key!, audioTypeStr),
          uploadOptions
        );
      }

      if (onSuccess) {
        onSuccess({
          ...pendingCase.data.case,
          status: CaseStatusType.enum.processing,
        });
      }
    }
    // For existing cases, just update metadata before upload
    else if (!createNewCase && caseId) {
      // Register toast for this case so it can be dismissed if case is deleted
      if (caseId) {
        registerCaseToast(caseId, toastId);
      }
      await axios.patch(`/api/cases/${caseId}`, {
        audioMetadata: createAudioMetadata(
          file,
          method,
          audioTypeStr,
          uploadOptions,
          supplementalAudio
        ),
      });

      // Get upload URL (needs caseId for interaction audio)
      const urlRequestData = createUrlRequestData(
        user,
        file,
        audioTypeStr,
        caseId,
        uploadOptions
      );
      const urlResponse = await axios.post(
        "/api/get-upload-url",
        urlRequestData
      );
      const uploadData = urlResponse.data;
      key = uploadData.key;

      // Upload main audio file
      await uploadAudioToS3(
        file,
        uploadData.uploadURL,
        (progress, remainingSeconds) => {
          updateToast(toastId, progress, remainingSeconds);
          onProgress?.(progress);
        },
        abortController.signal
      );

      // Update status to processing after upload
      // Note: audioUrl is NOT set on the case - it's stored in case_files instead
      const audioUrl = generateS3AudioUrl(key, audioTypeStr);
      await axios.patch(`/api/cases/${caseId}`, {
        status: "processing",
        content: { data: "Processing audio..." },
      });

      // Create case_files entry - this is where audio URL is stored
      const existingFileName =
        file instanceof File
          ? file.name
          : `recorded_audio_${user?.id}_${new Date().toISOString().split("T")[0]}.${getFileExtension(file)}`;
      singleFileId = await createCaseFileEntry(
        caseId,
        file,
        existingFileName,
        audioTypeStr,
        audioUrl,
        uploadOptions
      );

      if (setCaseContent) {
        setCaseContent({ data: "Processing audio..." });
      }
    }
    // For NARRATIVE audio with NEW cases, create case AFTER upload (existing logic)
    else if (createNewCase && audioTypeStr === "narrative") {
      // Get upload URL (needs caseId for interaction audio)
      const urlRequestData = createUrlRequestData(
        user,
        file,
        audioTypeStr,
        caseId,
        uploadOptions
      );
      const urlResponse = await axios.post(
        "/api/get-upload-url",
        urlRequestData
      );
      const uploadData = urlResponse.data;
      key = uploadData.key;

      // Upload main audio file
      await uploadAudioToS3(
        file,
        uploadData.uploadURL,
        (progress, remainingSeconds) => {
          updateToast(toastId, progress, remainingSeconds);
          onProgress?.(progress);
        },
        abortController.signal
      );

      // Note: audioUrl is NOT set on the case - it's stored in case_files instead
      const pendingCase = await axios.post("/api/cases", {
        caseNumber: `case-${Date.now()}`,
        incidentDate: new Date().toISOString().split("T")[0],
        incidentTime: new Date().toISOString().split("T")[1].split("Z")[0],
        status: "processing",
        content: { data: "Processing audio..." },
        audioMetadata: createAudioMetadata(
          file,
          method,
          audioTypeStr,
          uploadOptions,
          supplementalAudio
        ),
        hasAttachments: true, // Audio URL stored in case_files, not on case
        ...(reportTemplateId && { reportTemplateId }),
        ...(callSign && { callSign }),
        ...(investigationId && { investigationId }),
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
      });
      caseId = pendingCase.data.case.id;
      resetEditorForNewCase();

      // Register toast for this case so it can be dismissed if case is deleted
      if (caseId) {
        registerCaseToast(caseId, toastId);

        // Create case_files entry - this is where audio URL is stored
        const narrativeFileName =
          file instanceof File
            ? file.name
            : `recorded_audio_${user?.id}_${new Date().toISOString().split("T")[0]}.${getFileExtension(file)}`;
        singleFileId = await createCaseFileEntry(
          caseId,
          file,
          narrativeFileName,
          audioTypeStr,
          generateS3AudioUrl(key!, audioTypeStr),
          uploadOptions
        );
      }

      if (onSuccess) {
        onSuccess({
          ...pendingCase.data.case,
          status: CaseStatusType.enum.processing,
        });
      }
    } else {
      // This shouldn't happen, but handle it gracefully
      throw new Error("Invalid audio processing configuration");
    }

    if (!caseId) {
      throw new Error("No case ID available for audio processing");
    }

    // Handle supplemental audio upload if it exists
    if (supplementalAudio && audioTypeStr === "interaction") {
      // Create separate abort controller for supplemental upload
      const supplementalAbortController = new AbortController();

      // Helper to show supplemental cancelled state
      const showSupplementalCancelledState = (suppToastId: string) => {
        toast.custom(
          () =>
            React.createElement(UploadProgressToast, {
              progress: 0,
              fileName: supplementalAudio.name,
              onCancel: () => toast.dismiss(suppToastId),
              cancelled: true,
            }),
          { id: suppToastId, duration: Infinity }
        );
      };

      // Helper to update supplemental toast
      const updateSupplementalToast = (
        suppToastId: string,
        progress: number,
        remainingSeconds?: number
      ) => {
        toast.custom(
          () =>
            React.createElement(UploadProgressToast, {
              progress,
              fileName: supplementalAudio.name,
              onCancel: () => {
                supplementalAbortController.abort();
                showSupplementalCancelledState(suppToastId);
              },
              remainingSeconds,
            }),
          { id: suppToastId, duration: Infinity }
        );
      };

      // Create supplemental upload toast
      const supplementalToastId = toast.custom(
        (t) =>
          React.createElement(UploadProgressToast, {
            progress: 0,
            fileName: supplementalAudio.name,
            onCancel: () => {
              supplementalAbortController.abort();
              showSupplementalCancelledState(t.id);
            },
          }),
        { duration: Infinity }
      );

      try {
        await uploadSupplementalAudio(
          supplementalAudio,
          user,
          caseId,
          uploadOptions,
          (progress, remainingSeconds) => {
            updateSupplementalToast(
              supplementalToastId,
              progress,
              remainingSeconds
            );
          },
          supplementalAbortController.signal
        );

        // Dismiss supplemental toast on success
        toast.dismiss(supplementalToastId);
      } catch (supplementalError) {
        // Handle supplemental upload cancellation
        if (
          axios.isAxiosError(supplementalError) &&
          supplementalError.code === "ERR_CANCELED"
        ) {
          console.log("Supplemental upload cancelled by user");
          // Toast already shown, just continue with main audio processing
        } else {
          // Show error but don't stop main audio processing
          toast.error(
            "Supplemental audio upload failed, but main audio will continue processing",
            { id: supplementalToastId, duration: 5000 }
          );
        }
      }
    }

    // Start processing for narrative audio (interaction audio is handled by case creation endpoint)
    if (audioTypeStr === "narrative" && key) {
      await axios.post("/api/transcribe", {
        key,
        caseId: caseId,
        fileId: singleFileId, // Include fileId for case_files update
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
        uploadOptions: uploadOptions && {
          detailLevel: uploadOptions.detailLevel,
          additionalContext: uploadOptions.additionalContext,
        },
      });
    }

    // Remove beforeunload listener - upload complete
    window.removeEventListener("beforeunload", handleBeforeUnload);

    toast.loading("Audio uploaded successfully. Processing audio...", {
      id: toastId,
      duration: Infinity,
    });

    // Navigate to case if navigation function provided
    if (navigate) {
      navigate(`/cases/${caseId}`);
    }

    // Start polling for status updates
    pollProcessingStatus(caseId, {
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess(
            data.case
              ? {
                  case: data.case,
                  score: data.score,
                  suggestions: data.suggestions,
                  transcription: data.transcription,
                }
              : data
          );
        }
      },
      onError: (error) => {
        console.error("Processing failed:", error);
        onError?.(error);
      },
      toastId,
      successMessage: createNewCase ? "Case created" : "Audio processed",
      cleanupToast: cleanupCaseToast,
    });
  } catch (error) {
    // Remove beforeunload listener - upload failed/cancelled
    window.removeEventListener("beforeunload", handleBeforeUnload);

    // Handle upload cancellation
    if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
      console.log("Upload cancelled by user");

      // Show cancelled state in toast
      showCancelledState(toastId);

      // Update content and discard the case if it was created (only for new cases, not existing ones)
      if (caseId && createNewCase) {
        try {
          // First update the content so if discard fails, the message is clear
          await axios.patch(
            `/api/cases/${caseId}`,
            { content: { data: "Upload cancelled" } },
            { withCredentials: true }
          );
          console.log("Case content updated to 'Upload cancelled':", caseId);

          // Then discard the case
          await axios.patch(`/api/cases/${caseId}/discard`, {}, { withCredentials: true });
          console.log("Case discarded after upload cancellation:", caseId);
          // Clean up toast registry
          cleanupCaseToast(caseId);
          // Notify parent to handle case deletion (select next case, etc.)
          onCancelled?.(caseId);
        } catch (discardError) {
          console.error("Failed to update/discard case after cancellation:", discardError);
        }
      }

      return; // Exit gracefully
    }

    console.error("Error processing audio file:", error);
    logAudioError(error, "AudioProcessingService", "processAudioFile", file, {
      audioType: audioTypeStr,
      createNewCase,
      existingCaseId,
    });

    const errorInfo = getAudioErrorInfo(error);

    // Reset case status on error for existing cases
    if (!createNewCase && existingCaseId && setCaseContent) {
      setCaseContent({ data: "<h1></h1>" });
      try {
        await axios.patch(`/api/cases/${existingCaseId}`, {
          status: "active",
          content: { data: "<h1></h1>" },
          error: "Failed to process audio file",
        });
      } catch (resetError) {
        console.error("Error resetting case status:", resetError);
      }
    }

    // For components that can handle custom toast creation
    if (
      typeof window !== "undefined" &&
      (window as any).showCustomAudioErrorToast
    ) {
      (window as any).showCustomAudioErrorToast(
        errorInfo,
        () => downloadAudioFile(file, user),
        toastId
      );
    } else {
      // Fallback to simple error toast
      toast.error(errorInfo.errorMessage, { id: toastId });
    }

    onError?.(errorInfo.errorMessage);
  }
};

/**
 * Creates a new blank case
 */
export const createBlankCase = async (
  user: any,
  navigate?: (path: string) => void
) => {
  try {
    const response = await axios.post(
      "/api/cases",
      {
        caseNumber: ``,
        incidentDate: new Date().toISOString().split("T")[0],
        incidentTime: new Date().toISOString().split("T")[1].split("Z")[0],
        status: CaseStatusType.enum.active,
        content: {
          data: "<h1></h1>",
        },
        localDateTimeOffset: new Date()
          .toLocaleString()
          .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
      },
      { withCredentials: true }
    );

    if (response.status === 201) {
      if (navigate) {
        navigate(`/cases/${response.data.case.id}`);
      }
      return response.data.case;
    } else {
      console.error("Failed to create new case");
      return null;
    }
  } catch (error) {
    console.error("Error creating new case:", error);
    logAudioError(
      error,
      "AudioProcessingService",
      "createBlankCase",
      new Blob()
    );
    throw error;
  }
};

/**
 * Updates an existing case with blank content (for checklist completion)
 */
export const updateCaseWithBlankContent = async (
  caseId: string,
  setCaseContent?: (content: { data: string }) => void,
  navigate?: (path: string) => void
) => {
  try {
    const newContent = { data: "<h1></h1>" };

    await axios.patch(
      `/api/cases/${caseId}`,
      { content: newContent },
      { withCredentials: true }
    );

    // Update local content state immediately
    if (setCaseContent) {
      setCaseContent(newContent);
    }

    // Navigate to the existing case
    if (navigate) {
      navigate(`/cases/${caseId}`);
    }

    return true;
  } catch (error) {
    console.error("Error updating case:", error);
    logAudioError(
      error,
      "AudioProcessingService",
      "updateCaseWithBlankContent",
      new Blob(),
      { caseId }
    );
    throw error;
  }
};
